import { useState } from "react";

interface SiteSubcategory {
  id: string;
  category_name: string;
  display_name?: string;
  product_count: number;
  source_type: string;
  mapped?: boolean;
}

interface Category {
  id: number | string;
  category_name: string;
  display_name?: string; // Translated name if available
  relative_demand_score: number;
  avg_monthly_searches: number;
  trend_yoy_pct?: number;
  children?: Category[];
  // Hybrid data fields
  product_count?: number;
  enriched_count?: number; // AI-enriched products count
  site_subcategories?: SiteSubcategory[];
  subcategory_count?: number;
  source_type?: string;
  sector_category_id?: number;
}

interface DemandWheelProps {
  data: Category[];
  onCategoryClick: (category: Category) => void;
  centerLabel: string;
  centerScore?: number;
  scoreLabel?: string; // Label for the score (e.g., "Demand Score", "Products", "Items")
  showProductRing?: boolean; // Show outer ring with product counts
}

export const DemandWheel = ({ data, onCategoryClick, centerLabel, centerScore, scoreLabel = "Demand Score", showProductRing = false }: DemandWheelProps) => {
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);

  // Check if we have any product data to show
  const hasProductData = data.some(cat => (cat.product_count ?? 0) > 0);
  const shouldShowProductRing = showProductRing || hasProductData;

  // Calculate max product count for scaling the outer ring
  const maxProductCount = Math.max(...data.map(cat => cat.product_count ?? 0), 1);

  // Much larger SVG for better readability
  const centerX = 900;
  const centerY = 900;
  const innerRadius = 150;  // Smaller inner circle
  const demandRingInner = 460; // Inner edge of demand ring
  const demandRingOuter = 500; // Outer edge of demand ring
  const productRingInner = shouldShowProductRing ? 510 : 0; // Inner edge of product ring (gap of 10)
  const productRingMaxOuter = shouldShowProductRing ? 600 : 0; // Max outer edge of product ring
  const textRadius = 430;   // Text closer to outer ring

  // Keep backwards compatibility
  const outerRadius = demandRingOuter;

  const getColorForScore = (score: number) => {
    // Using RGB values that work in both light and dark mode
    if (score >= 70) return "rgb(34 197 94)"; // green-500
    if (score >= 50) return "rgb(234 179 8)"; // yellow-500
    return "rgb(239 68 68)"; // red-500
  };

  // Helper function to wrap text into multiple lines
  const wrapText = (text: string, maxCharsPerLine: number = 18): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length > maxCharsPerLine && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    // Limit to 2 lines for center text
    return lines.slice(0, 2);
  };

  // Helper function to calculate font size and truncate text for category labels
  // Available space is from textRadius (430) to innerRadius + padding (150 + 40 = 190)
  // This gives us ~240px of space, which at 14px font is roughly 40-45 characters
  const formatCategoryLabel = (text: string): { text: string; fontSize: number } => {
    const maxChars = 45; // Maximum characters before truncation
    const baseFontSize = 16;
    const minFontSize = 12;

    // For shorter text, use full font size
    if (text.length <= 25) {
      return { text, fontSize: baseFontSize };
    }

    // For medium text (25-35 chars), use slightly smaller font
    if (text.length <= 35) {
      return { text, fontSize: 14 };
    }

    // For longer text (35-45 chars), use minimum font size
    if (text.length <= maxChars) {
      return { text, fontSize: minFontSize };
    }

    // For very long text, truncate and use minimum font size
    const truncated = text.substring(0, maxChars - 3) + '...';
    return { text: truncated, fontSize: minFontSize };
  };

  const polarToCartesian = (angle: number, radius: number) => {
    const rad = (angle - 90) * Math.PI / 180;
    return {
      x: centerX + radius * Math.cos(rad),
      y: centerY + radius * Math.sin(rad)
    };
  };

  const totalSegments = data.length;
  const anglePerSegment = 360 / totalSegments;
  // Gap between segments: fixed 1° for small sets, proportional for large sets
  const segmentGap = Math.min(1, anglePerSegment * 0.15);

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <svg
        viewBox="0 0 1800 1800"
        className="animate-zoom-in"
        style={{ width: "100%", height: "auto", maxWidth: "1200px", userSelect: "none" }}
      >
        {/* Center circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius}
          fill="hsl(var(--primary))"
          className="drop-shadow-lg"
        />

        {/* Center text - multi-line support */}
        {wrapText(centerLabel).map((line, index, arr) => {
          const totalLines = arr.length;
          const lineHeight = 28;
          const startY = centerY - 30 - ((totalLines - 1) * lineHeight) / 2;

          return (
            <text
              key={index}
              x={centerX}
              y={startY + (index * lineHeight)}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="24"
              fontWeight="600"
              className="fill-primary-foreground"
            >
              {line}
            </text>
          );
        })}
        {centerScore !== undefined && (
          <>
            <text
              x={centerX}
              y={centerY + 20}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="48"
              fontWeight="700"
              className="fill-primary-foreground"
            >
              {centerScore.toLocaleString()}
            </text>
            <text
              x={centerX}
              y={centerY + 60}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="16"
              className="fill-primary-foreground"
              opacity="0.8"
            >
              searches/mo
            </text>
          </>
        )}

        {/* Segments */}
        {data.map((category, index) => {
          const angle = index * anglePerSegment;
          const midAngle = angle + anglePerSegment / 2;
          const startAngle = angle;
          const endAngle = angle + anglePerSegment - segmentGap;

          const start = polarToCartesian(startAngle, outerRadius);
          const end = polarToCartesian(endAngle, outerRadius);
          const innerStart = polarToCartesian(startAngle, outerRadius - 40);
          const innerEnd = polarToCartesian(endAngle, outerRadius - 40);

          const largeArcFlag = anglePerSegment > 180 ? 1 : 0;

          const pathData = [
            `M ${start.x} ${start.y}`,
            `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
            `L ${innerEnd.x} ${innerEnd.y}`,
            `A ${outerRadius - 40} ${outerRadius - 40} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
            'Z'
          ].join(' ');

          // Connecting line - from inner circle to inner edge of outer ring
          const lineStart = polarToCartesian(midAngle, innerRadius + 10);
          const lineEnd = polarToCartesian(midAngle, outerRadius - 40);

          // Text positioning logic (same as QuestionWheel)
          let rotation: number;
          let textAnchor: "start" | "end";

          // Left side of circle (180° to 360°): start from inside, read outward
          if (midAngle > 180 && midAngle <= 360) {
            rotation = midAngle + 90;
            textAnchor = "start";
          }
          // Right side of circle (0° to 180°): end at inside position
          else {
            rotation = midAngle - 90;
            textAnchor = "end";
          }

          const textPos = polarToCartesian(midAngle, textRadius);
          const scorePos = polarToCartesian(midAngle, outerRadius - 20);

          const isHovered = hoveredSegment === index;
          const color = getColorForScore(category.relative_demand_score);

          return (
            <g key={category.id} className="animate-wheel">
              {/* Connecting line */}
              <line
                x1={lineStart.x}
                y1={lineStart.y}
                x2={lineEnd.x}
                y2={lineEnd.y}
                stroke="hsl(var(--muted-foreground))"
                strokeWidth="1"
                opacity="0.3"
              />

              {/* Colored segment */}
              <path
                d={pathData}
                fill={color}
                stroke="hsl(var(--background))"
                strokeWidth="2"
                className="cursor-pointer transition-all duration-300"
                style={{
                  filter: isHovered ? "brightness(1.2)" : "none",
                  opacity: isHovered ? 0.9 : 0.7
                }}
                onMouseEnter={() => setHoveredSegment(index)}
                onMouseLeave={() => setHoveredSegment(null)}
                onClick={() => onCategoryClick(category)}
              />

              {/* Category text - positioned outside ring with dynamic sizing */}
              {(() => {
                const labelText = category.display_name || category.category_name;
                const { text: formattedText, fontSize } = formatCategoryLabel(labelText);
                return (
                  <text
                    x={textPos.x}
                    y={textPos.y}
                    fontSize={fontSize}
                    fill="hsl(var(--foreground))"
                    textAnchor={textAnchor}
                    transform={`rotate(${rotation}, ${textPos.x}, ${textPos.y})`}
                    className="font-medium cursor-pointer hover:font-bold transition-all"
                    opacity="0.85"
                    onMouseEnter={() => setHoveredSegment(index)}
                    onMouseLeave={() => setHoveredSegment(null)}
                    onClick={() => onCategoryClick(category)}
                    style={{ cursor: 'pointer', pointerEvents: 'all' }}
                  >
                    <title>{labelText}</title>
                    {formattedText}
                  </text>
                );
              })()}

              {/* Score number on ring - only show if > 0 */}
              {category.relative_demand_score > 0 && (
                <text
                  x={scorePos.x}
                  y={scorePos.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="18"
                  fontWeight="bold"
                  fill="hsl(var(--background))"
                  className="cursor-pointer"
                  onMouseEnter={() => setHoveredSegment(index)}
                  onMouseLeave={() => setHoveredSegment(null)}
                  onClick={() => onCategoryClick(category)}
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                >
                  {category.relative_demand_score}
                </text>
              )}

              {/* Product count outer ring - variable height based on product count */}
              {shouldShowProductRing && (category.product_count ?? 0) > 0 && (() => {
                const productCount = category.product_count ?? 0;
                // Scale the outer radius based on product count (min 35px, max based on proportion)
                const ringHeight = Math.max(35, Math.min(90, (productCount / maxProductCount) * 90));
                const productOuterRadius = productRingInner + ringHeight;

                const prodStart = polarToCartesian(startAngle, productOuterRadius);
                const prodEnd = polarToCartesian(endAngle, productOuterRadius);
                const prodInnerStart = polarToCartesian(startAngle, productRingInner);
                const prodInnerEnd = polarToCartesian(endAngle, productRingInner);

                const productPath = [
                  `M ${prodStart.x} ${prodStart.y}`,
                  `A ${productOuterRadius} ${productOuterRadius} 0 ${largeArcFlag} 1 ${prodEnd.x} ${prodEnd.y}`,
                  `L ${prodInnerEnd.x} ${prodInnerEnd.y}`,
                  `A ${productRingInner} ${productRingInner} 0 ${largeArcFlag} 0 ${prodInnerStart.x} ${prodInnerStart.y}`,
                  'Z'
                ].join(' ');

                const productTextPos = polarToCartesian(midAngle, productRingInner + ringHeight / 2);
                // Adaptive font size based on ring height
                const fontSize = ringHeight >= 50 ? 14 : ringHeight >= 40 ? 12 : 10;

                return (
                  <>
                    <path
                      d={productPath}
                      fill="hsl(var(--primary))"
                      stroke="hsl(var(--background))"
                      strokeWidth="2"
                      className="cursor-pointer transition-all duration-300"
                      style={{
                        filter: isHovered ? "brightness(1.2)" : "none",
                        opacity: isHovered ? 0.9 : 0.6
                      }}
                      onMouseEnter={() => setHoveredSegment(index)}
                      onMouseLeave={() => setHoveredSegment(null)}
                      onClick={() => onCategoryClick(category)}
                    />
                    {/* Product count number - show when segment is wide enough */}
                    {anglePerSegment >= 2.5 && (
                    <text
                      x={productTextPos.x}
                      y={productTextPos.y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={fontSize}
                      fontWeight="bold"
                      fill="hsl(var(--primary-foreground))"
                      className="cursor-pointer"
                      onMouseEnter={() => setHoveredSegment(index)}
                      onMouseLeave={() => setHoveredSegment(null)}
                      onClick={() => onCategoryClick(category)}
                      style={{ cursor: 'pointer', pointerEvents: 'all' }}
                    >
                      {productCount}
                    </text>
                    )}
                  </>
                );
              })()}
            </g>
          );
        })}

        {/* Hover tooltip */}
        {hoveredSegment !== null && (() => {
          const category = data[hoveredSegment];
          if (!category) return null;

          const angle = hoveredSegment * anglePerSegment + anglePerSegment / 2;
          const angleRad = (angle - 90) * Math.PI / 180;

          // Position tooltip outside the text (further out if we have product ring)
          const tooltipRadius = shouldShowProductRing ? 720 : 600;
          const tooltipSvgX = centerX + tooltipRadius * Math.cos(angleRad);
          const tooltipSvgY = centerY + tooltipRadius * Math.sin(angleRad);

          // Calculate tooltip height based on content
          const hasSubcategories = (category.site_subcategories?.length ?? 0) > 0;
          const hasProductCount = (category.product_count ?? 0) > 0;
          const hasEnrichmentData = hasProductCount && category.enriched_count !== undefined;
          const baseHeight = 140;
          const subcatHeight = hasSubcategories ? Math.min(category.site_subcategories!.length * 20 + 40, 120) : 0;
          const productHeight = hasProductCount ? 24 : 0;
          const enrichmentHeight = hasEnrichmentData ? 24 : 0;

          const w = 300;
          const h = baseHeight + subcatHeight + productHeight + enrichmentHeight;
          const x = tooltipSvgX - w / 2;
          const y = tooltipSvgY - h / 2;

          return (
            <foreignObject x={x} y={y} width={w} height={h}>
              <div className="bg-card border border-border rounded-lg shadow-xl p-4 animate-fade-in pointer-events-none">
                <div className="text-sm space-y-1">
                  <div className="font-semibold text-foreground text-base">{category.display_name || category.category_name}</div>

                  {/* Product count - prominent display */}
                  {hasProductCount && (
                    <div className="text-primary font-medium">
                      Products: <span className="font-bold">{category.product_count?.toLocaleString()}</span>
                    </div>
                  )}

                  {/* AI Enrichment stats */}
                  {hasEnrichmentData && (() => {
                    const enrichedCount = category.enriched_count || 0;
                    const productCount = category.product_count || 0;
                    const enrichmentPercent = productCount > 0 ? Math.round((enrichedCount / productCount) * 100) : 0;
                    const colorClass = enrichmentPercent === 100
                      ? 'text-green-600 dark:text-green-400'
                      : enrichmentPercent > 50
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-red-600 dark:text-red-400';
                    return (
                      <div className={`font-medium ${colorClass}`}>
                        AI Enriched: <span className="font-bold">{enrichedCount}/{productCount}</span> ({enrichmentPercent}%)
                      </div>
                    );
                  })()}

                  <div className="text-muted-foreground">
                    {scoreLabel}: <span className="font-medium">{category.relative_demand_score.toLocaleString()}</span>
                  </div>
                  <div className="text-muted-foreground">
                    Searches: <span className="font-medium">{category.avg_monthly_searches.toLocaleString()}/mo</span>
                  </div>
                  {category.trend_yoy_pct !== undefined && (
                    <div className={category.trend_yoy_pct > 0 ? "text-success" : "text-destructive"}>
                      YoY Trend: {category.trend_yoy_pct > 0 ? "+" : ""}{category.trend_yoy_pct}%
                    </div>
                  )}

                  {/* Site subcategories list */}
                  {hasSubcategories && (
                    <div className="mt-2 pt-2 border-t">
                      <div className="text-xs font-medium text-muted-foreground mb-1">
                        Site Subcategories ({category.site_subcategories!.length}):
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {category.site_subcategories!.slice(0, 5).map((sub) => (
                          <span
                            key={sub.id}
                            className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded"
                          >
                            {sub.display_name || sub.category_name} ({sub.product_count})
                          </span>
                        ))}
                        {category.site_subcategories!.length > 5 && (
                          <span className="text-xs text-muted-foreground">
                            +{category.site_subcategories!.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-muted-foreground italic mt-2 pt-2 border-t">
                    Click to explore subcategories
                  </div>
                </div>
              </div>
            </foreignObject>
          );
        })()}
      </svg>
    </div>
  );
};
